import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import type { OTranscriptProps } from '@ai/ui-components'
import type { TimelineItem, TranscriptGroup, TranscriptSegment } from '@/types/LivekitContext'

type TranscriptItem = OTranscriptProps['transcript'][number]
type TranscriptEvent = NonNullable<OTranscriptProps['events']>[number]

type TranscriptSource = MaybeRefOrGetter<TranscriptGroup[] | null | undefined>
type TimelineSource = MaybeRefOrGetter<TimelineItem[] | null | undefined>
type AudioSource = MaybeRefOrGetter<OTranscriptProps['audio']>

const SUPPORTED_EVENT_TYPES = new Set<TranscriptEvent['type']>(['va', 'var', 'tool'])

function uniqueValues(values: (string | null | undefined)[]) {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value?.trim()))))
}

function getSegmentId(group: TranscriptGroup, segment: TranscriptSegment, index: number) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return segment.segment_id ?? segment.id ?? `${group.id}-${String(index)}`
}

function getSegmentBadges(group: TranscriptGroup, segment: TranscriptSegment) {
  return uniqueValues([
    ...(segment.flags ?? []),
    ...(group.segmentsStatuses ?? []).map((status) => status.text),
  ])
}

function normalizeEventType(type: string): TranscriptEvent['type'] | null {
  if (SUPPORTED_EVENT_TYPES.has(type as TranscriptEvent['type'])) {
    return type as TranscriptEvent['type']
  }

  console.warn('[OTranscript] Unsupported timeline event type:', type)
  return null
}

function findSegmentIdAtTime(transcript: TranscriptItem[], timeSec: number) {
  const exact = transcript.find((item) => timeSec >= item.startSec && timeSec <= item.endSec)
  if (exact) return exact.id

  return transcript.find((item) => Math.abs(item.startSec - timeSec) <= 1)?.id
}

function parseTimelineTimeSec(value: string | null | undefined) {
  if (!value) return 0

  const parts = value
    .split(':')
    .map((part) => Number(part.trim()))
    .filter((part) => Number.isFinite(part))

  if (parts.length === 0) return 0

  const [seconds = 0, minutes = 0, hours = 0] = parts.reverse()
  return Math.max(0, hours * 3600 + minutes * 60 + seconds)
}

function mapTranscriptGroupsToTranscriptItems(
  groups: TranscriptGroup[] | null | undefined,
): OTranscriptProps['transcript'] {
  return (groups ?? []).flatMap((group) =>
    group.segments.map((segment, index) => {
      const badges = getSegmentBadges(group, segment)

      return {
        id: getSegmentId(group, segment, index),
        speaker: segment.is_external ? 'customer' : 'agent',
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        text: segment.text ?? '',
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        startSec: Math.max(0, segment.start_time ?? 0),
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        endSec: Math.max(0, segment.end_time ?? segment.start_time ?? 0),
        ...(badges.length > 0 ? { badges } : {}),
      }
    }),
  )
}

function mapTimelineItemsToTranscriptEvents(
  items: TimelineItem[] | null | undefined,
  transcript: OTranscriptProps['transcript'] = [],
): NonNullable<OTranscriptProps['events']> {
  return (items ?? []).flatMap((item, index) => {
    const type = normalizeEventType(item.type)
    if (!type) return []

    const timeSec = parseTimelineTimeSec(item.time)
    const segmentId = findSegmentIdAtTime(transcript, timeSec)
    // Timeline uses title as the event category and detail as the concrete value.
    // OTranscript displays the concrete value as label and the category as supporting detail.
    const eventLabel = item.detail || item.title
    const eventCategory = item.detail ? item.title : undefined

    return [
      {
        id: item.id || `event-${String(index)}`,
        type,
        timeSec,
        label: eventLabel,
        ...(eventCategory ? { detail: eventCategory } : {}),
        ...(segmentId ? { segmentId } : {}),
      },
    ]
  })
}

export function useOTranscriptContext(options: {
  transcripts: TranscriptSource
  events?: TimelineSource
  audio?: AudioSource
}) {
  const transcript = computed<OTranscriptProps['transcript']>(() =>
    mapTranscriptGroupsToTranscriptItems(toValue(options.transcripts)),
  )
  const events = computed<NonNullable<OTranscriptProps['events']>>(() =>
    mapTimelineItemsToTranscriptEvents(toValue(options.events) ?? [], transcript.value),
  )
  const audio = computed<OTranscriptProps['audio']>(() => toValue(options.audio) ?? null)

  return {
    audio,
    transcript,
    events,
  }
}
